import React, { useMemo, useState } from 'react';
import { Form } from 'react-final-form';

import { Page, useAppLayoutContextConsumer } from '@occc-app/design-system';
import { Button, Tab, Tabs, TabContext, TabPanel } from '@occc/ui-design-system';
import { useUserSelector } from '@occc-app/App/AppContext';
import { featureFlagEditor, FeatureFlagEditor } from '@occc/feat-feature-flag-control';
import { useFlagEnabled } from '@occc/data-flags';
import { OpenCommerceUserFragment, Open_Commerce_User_Status } from '@occc/graphql';

import { UpdateUserFormRenderComponent } from './UpdateUserFormRenderComponent';
import { breadcrumbLinks } from './constants';

import {
  UserFormInitialValues,
  UserFormProps,
  UserFormRenderProps,
  UserFormValues,
} from '../types';
import { validateUser } from '../validation';

type UpdateUserTab = 'general' | 'feature-access';

interface UpdateUserPageProps {
  onSubmit: UserFormProps['onSubmit'];
  loading: boolean;
  initialValues: UserFormInitialValues;
  user: OpenCommerceUserFragment;
  onEnable: () => void;
  onCancel: () => void;
}

export const UpdateUserPage: React.FC<UpdateUserPageProps> = ({
  initialValues,
  onSubmit,
  loading,
  onEnable,
  onCancel,
  user,
}) => {
  const currentUser = useUserSelector();
  const editorEnabled = useFlagEnabled(featureFlagEditor);
  const [activeTab, setActiveTab] = useState<UpdateUserTab>('general');

  useAppLayoutContextConsumer(breadcrumbLinks);

  const renderForm: UserFormRenderProps['render'] = React.useCallback(
    ({ handleSubmit, valid, pristine, submitting }: UserFormRenderProps) => {
      return (
        <UpdateUserFormRenderComponent
          handleSubmit={handleSubmit}
          disabled={!valid || pristine || submitting || loading}
          loading={loading}
        />
      );
    },
    [loading],
  );

  const actions = useMemo(() => {
    if (user.status === Open_Commerce_User_Status.Disabled) {
      return (
        <Button variant='contained' color='secondary' onClick={onEnable}>
          Enable
        </Button>
      );
    }

    return null;
  }, [user.status, onEnable]);

  const handleTabChange = (_event: React.SyntheticEvent, value: UpdateUserTab) => {
    setActiveTab(value);
  };

  return (
    <Page
      browserTabTitle='Update User'
      pageHeaderProps={{ title: 'User Update' }}
      containerProps={{ variant: 'md' }}
      actions={actions}
    >
      {editorEnabled ? (
        <TabContext value={activeTab}>
          <Tabs color='secondary' value={activeTab} onChange={handleTabChange}>
            <Tab value={'general' satisfies UpdateUserTab} label='General' />
            <Tab value={'feature-access' satisfies UpdateUserTab} label='Feature Access' />
          </Tabs>
          <TabPanel value={'general' satisfies UpdateUserTab} sx={{ p: 0 }}>
            <Form<UserFormValues, UserFormInitialValues>
              initialValues={initialValues}
              onSubmit={onSubmit}
              render={renderForm}
              validate={validateUser}
            />
          </TabPanel>
          <TabPanel value={'feature-access' satisfies UpdateUserTab} sx={{ p: 0 }}>
            <FeatureFlagEditor
              userUuid={user.uuid}
              currentUserUuid={currentUser!.uuid}
              targetUserRole={user.userRole}
              onCancel={onCancel}
            />
          </TabPanel>
        </TabContext>
      ) : (
        <Form<UserFormValues, UserFormInitialValues>
          initialValues={initialValues}
          onSubmit={onSubmit}
          render={renderForm}
          validate={validateUser}
        />
      )}
    </Page>
  );
};
